import React, { useState } from "react";
import { PlayCircle, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import "./ResultTopperStories.css";

const toppers = [
  {
    id: 1,
    name: "Anjali Verma",
    photo: "https://randomuser.me/api/portraits/women/68.jpg",
    videoUrl: "https://www.youtube.com/embed/tgbNymZ7vqY",
    caseStudy:
      "Anjali overcame financial challenges through sheer dedication, supported by our personalized mentorship program. Her strategy combined rigorous revision with targeted test practice.",
    quote:
      "Success is a journey — the institute helped me navigate every step with clarity and confidence.",
  },
  {
    id: 2,
    name: "Rajesh Kumar",
    photo: "https://randomuser.me/api/portraits/men/72.jpg",
    videoUrl: "https://www.youtube.com/embed/ysz5S6PUM-U",
    caseStudy:
      "Rajesh focused on time management and stress control, benefiting greatly from our weekly doubt clearing sessions and interview preparation workshops.",
    quote:
      "The support from faculty and peers created a community where I never felt alone in the struggle.",
  },
  {
    id: 3,
    name: "Pooja Singh",
    photo: "https://randomuser.me/api/portraits/women/43.jpg",
    videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
    caseStudy:
      "Pooja’s persistence and strategic approach were amplified by our customized syllabus and mock tests, paving her way to success.",
    quote:
      "This institute didn’t just teach me syllabus — it taught me how to learn and believe.",
  },
];

export default function ResultTopperStories() {
  const [currentVideo, setCurrentVideo] = useState(toppers[0].id);
  const [expandedId, setExpandedId] = useState(null);

  const selectedTopper = toppers.find((t) => t.id === currentVideo);

  const handlePrev = () => {
    const idx = toppers.findIndex((t) => t.id === currentVideo);
    const prevIdx = (idx - 1 + toppers.length) % toppers.length;
    setCurrentVideo(toppers[prevIdx].id);
  };

  const handleNext = () => {
    const idx = toppers.findIndex((t) => t.id === currentVideo);
    const nextIdx = (idx + 1) % toppers.length;
    setCurrentVideo(toppers[nextIdx].id);
  };

  return (
    <section className="result-topper-stories-container" aria-labelledby="topper-stories-title">
      <header className="result-topper-stories-header">
        <h2 id="topper-stories-title">Toppers’ Inspiring Stories & Interviews</h2>
        <p>Learn from the journeys and experiences of our successful students.</p>
      </header>

      <div className="result-topper-video-section" aria-label="Video interviews carousel">
        <button
          className="result-topper-nav-btn"
          aria-label="Previous topper video"
          onClick={handlePrev}
        >
          <ChevronLeft size={28} />
        </button>

        <div className="result-topper-video-wrapper">
          <iframe
            key={selectedTopper.id}
            src={selectedTopper.videoUrl}
            title={`Interview video of ${selectedTopper.name}`}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            loading="lazy"
          ></iframe>
          <div className="result-topper-video-name">
            <img
              src={selectedTopper.photo}
              alt={`${selectedTopper.name}`}
              className="result-topper-video-photo"
              loading="lazy"
            />
            <span>{selectedTopper.name}</span>
            <PlayCircle className="result-topper-video-icon" size={20} />
          </div>
        </div>

        <button
          className="result-topper-nav-btn"
          aria-label="Next topper video"
          onClick={handleNext}
        >
          <ChevronRight size={28} />
        </button>
      </div>

      <div className="result-topper-case-studies">
        {toppers.map(({ id, name, photo, caseStudy }) => (
          <article
            key={id}
            className={`result-topper-case-study ${expandedId === id ? "expanded" : ""}`}
          >
            <div
              className="result-topper-case-study-header"
              role="button"
              tabIndex={0}
              onClick={() => setExpandedId(expandedId === id ? null : id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setExpandedId(expandedId === id ? null : id);
                }
              }}
              aria-expanded={expandedId === id}
              aria-controls={`case-study-content-${id}`}
            >
              <img src={photo} alt={`Photo of topper ${name}`} loading="lazy" />
              <h3>{name}</h3>
              <button
                className="result-topper-expand-btn"
                aria-label={expandedId === id ? `Collapse ${name} story` : `Expand ${name} story`}
              >
                {expandedId === id ? "−" : "+"}
              </button>
            </div>

            <div
              id={`case-study-content-${id}`}
              className="result-topper-case-study-content"
              aria-hidden={expandedId !== id}
            >
              <p>{caseStudy}</p>
            </div>
          </article>
        ))}
      </div>

      <div className="result-topper-quotes">
        <h3>Motivational Quotes from Our Toppers</h3>
        <div className="result-topper-quotes-list">
          {toppers.map(({ id, name, photo, quote }) => (
            <figure key={id} className="result-topper-quote-item">
              <blockquote>
                <Quote size={20} className="result-topper-quote-icon" />
                <p>{quote}</p>
              </blockquote>
              <figcaption>
                <img src={photo} alt={name} loading="lazy" />
                <cite>{name}</cite>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
